import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { FirebaseService, FirebaseRealtimeEvent } from './firebase.service';
import { MobileChatApiService } from './mobile-chat-api.service';

export type MessageBy = 'teacher' | 'client' | 'student';

export interface ChatMessage {
  id: number | string;
  name: string;
  img: string;
  date: string;
  time: string;
  message: string;
  recipient: string;
  isSent?: boolean; // true for sent, false for received
  type?: 'text' | 'image' | 'audio' | 'file'; // message type
  fileUrl?: string; // for images/files
  fileName?: string; // for files
  audioBlob?: Blob; // for audio messages
  status?: 'sending' | 'sent' | 'delivered' | 'read'; // message status
  isSeen?: boolean; // true if recipient has seen the message
  replyTo?: ChatMessage; // reply to another message
  reactions?: { emoji: string; count: number; users: string[] }[]; // emoji reactions
  isTyping?: boolean; // typing indicator
  firebaseKey?: string;
  timestamp?: number | string;
}

export interface ChatRoom {
  id: string;
  name: string;
  participants: string[];
  lastMessage?: ChatMessage;
  unreadCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService implements OnDestroy {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$ = this.messagesSubject.asObservable();

  private currentRoomId: string | null  = null;
  private currentUserId: string = '';
  private currentStudentId: string | null = null;
  private currentUserType: MessageBy = 'teacher';
  private firebaseListenFrom: number | null = null;
  private seenFirebaseMessageIds = new Set<string>();
  private firebaseSubscription: Subscription = new Subscription();
  private isRealtimeListening = false;
  private realtimeListenerKey: string | null = null;
  private pendingSendKeys = new Set<string>();

  private typingUsersSubject = new BehaviorSubject<{ [userId: string]: boolean }>({});
  public typingUsers$ = this.typingUsersSubject.asObservable();

  constructor(
    private firebaseService: FirebaseService,
    private mobileChatApi: MobileChatApiService
  ) {}

  getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  // Send message using hybrid architecture
  async sendMessage(message: string, type: 'text' | 'image' | 'file' | 'audio' = 'text', fileData?: { url?: string; name?: string }): Promise<void> {
    if (!this.currentStudentId) {
      throw new Error('No student selected');
    }

    const payloadMessage = this.buildMessagePayload(message, type, fileData);
    const sendKey = this.buildSendKey(payloadMessage, type, fileData);
    if (this.pendingSendKeys.has(sendKey)) {
      return;
    }

    this.pendingSendKeys.add(sendKey);
    const optimisticId = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const optimisticTimestamp = Date.now();
    const optimisticMessage: ChatMessage = {
      id: optimisticId,
      name: 'You',
      img: '',
      date: new Date(optimisticTimestamp).toISOString().split('T')[0],
      time: new Date(optimisticTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      message,
      recipient: 'Chat',
      isSent: true,
      type,
      fileUrl: fileData?.url,
      fileName: fileData?.name,
      status: 'sending',
      timestamp: optimisticTimestamp
    };
    this.upsertSortedMessage(optimisticMessage);

    const payload: Parameters<MobileChatApiService['sendTeacherMessage']>[0] = {
      student_id: this.currentStudentId,
      message: payloadMessage,
      message_by: this.currentUserType,
      feedback_taken: false
    };

    try {
      const response = await firstValueFrom(this.mobileChatApi.sendTeacherMessage(payload));

      const sessionId = (response as any)?.data?.session_id;
      if (sessionId && !this.currentRoomId) {
        const newRoomId = sessionId.toString();
        this.currentRoomId = newRoomId;
        this.firebaseListenFrom = null;
        this.firebaseService.initializeRoom(this.currentUserId, newRoomId);
        this.startRealtimeListeners();
      }

      this.updateMessageStatus(optimisticId, 'sent');
    } catch (error) {
      console.error('Failed to send message:', error);
      this.removeMessage(optimisticId);
      throw error;
    } finally {
      this.pendingSendKeys.delete(sendKey);
    }
  }

  // Legacy method for backward compatibility
  addMessage(message: string, isSent: boolean = true, replyTo?: ChatMessage) {
    const newMessage: ChatMessage = {
      id: Date.now(),
      name: isSent ? 'You' : 'Shalil Jaiswar',
      img: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      message,
      recipient: 'Chat',
      isSent,
      type: 'text',
      status: isSent ? 'sending' : 'delivered',
      replyTo
    };
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, newMessage]);

    // Simulate message status progression for sent messages
    if (isSent) {
      setTimeout(() => this.updateMessageStatus(newMessage.id, 'sent'), 500);
      setTimeout(() => this.updateMessageStatus(newMessage.id, 'delivered'), 1000);
      setTimeout(() => this.updateMessageStatus(newMessage.id, 'read'), 2000);
    }
  }

  addImageMessage(imageUrl: string, fileName: string, isSent: boolean = true) {
    const newMessage: ChatMessage = {
      id: Date.now(),
      name: isSent ? 'You' : 'Shalil Jaiswar',
      img: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      message: `📷 ${fileName}`,
      recipient: 'Chat',
      isSent,
      type: 'image',
      fileUrl: imageUrl,
      fileName
    };
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, newMessage]);
  }

  addFileMessage(fileName: string, fileUrl: string, isSent: boolean = true) {
    const newMessage: ChatMessage = {
      id: Date.now(),
      name: isSent ? 'You' : 'Shalil Jaiswar',
      img: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      message: `📎 ${fileName}`,
      recipient: 'Chat',
      isSent,
      type: 'file',
      fileUrl,
      fileName
    };
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, newMessage]);
  }

  addAudioMessage(audioBlob: Blob, isSent: boolean = true) {
    const newMessage: ChatMessage = {
      id: Date.now(),
      name: isSent ? 'You' : 'Shalil Jaiswar',
      img: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      message: '🎵 Voice message',
      recipient: 'Chat',
      isSent,
      type: 'audio',
      audioBlob
    };
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, newMessage]);
  }

  updateMessageStatus(messageId: number | string, status: 'sending' | 'sent' | 'delivered' | 'read') {
    const id = messageId;
    const currentMessages = this.messagesSubject.value;
    const updatedMessages = currentMessages.map(message =>
      message.id === id ? { ...message, status } : message
    );
    this.messagesSubject.next(updatedMessages);
  }

  // Initialize room with backend + realtime
  initializeRoom(studentId: string | number, userId: string | number, userType: MessageBy = 'teacher') {
    const nextStudentId = studentId?.toString() ?? null;
    const nextUserId = userId?.toString() ?? '';

    if (
      this.currentStudentId === nextStudentId &&
      this.currentUserId === nextUserId &&
      this.currentUserType === (userType || 'teacher') &&
      this.isRealtimeListening
    ) {
      return;
    }

    this.resetRoom();
    this.currentStudentId = nextStudentId;
    this.currentUserId = nextUserId;
    this.currentUserType = userType || 'teacher';

    // Initialize Firebase presence
    this.firebaseService.initializeRoom(this.currentUserId, this.currentRoomId ?? '');

    this.firebaseListenFrom = null;
    this.startRealtimeListeners();

    // Load message history and active session
    this.loadMessages();
  }

  // Load messages from backend API
  private loadMessages() {
    if (!this.currentUserId || !this.currentStudentId) return;

    this.mobileChatApi.getChatHistoryByUserStudent(this.currentUserId, this.currentStudentId).subscribe({
      next: (response: any) => {
        const sessions = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data ?? [];
        if (Array.isArray(sessions) && sessions.length) {
          const activeSession = sessions[sessions.length - 1];
          this.currentRoomId = activeSession?.id?.toString() ?? null;
          if (this.currentRoomId) {
            this.firebaseService.initializeRoom(this.currentUserId, this.currentRoomId);
          }
          const history = activeSession?.chat_history ?? [];

          const messages: ChatMessage[] = history.map((msg: any) => {
            const parsed = this.parseMessagePayload(msg.message);
            const messageDate = msg.message_date || msg.created_at || msg.updated_at;
            const dateObj = this.parseApiDate(messageDate);
            return {
              id: msg.id ?? `${msg.chat_session_id}-${msg.sequence_no}`,
              name: msg.message_by === 'student' ? 'Student' : 'You',
              img: '',
              date: dateObj.toISOString().split('T')[0],
              time: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              message: parsed.text,
              recipient: 'Chat',
              isSent: msg.message_by !== 'student',
              type: parsed.type,
              fileUrl: parsed.fileUrl,
              fileName: parsed.fileName,
              status: msg.message_by !== 'student' ? 'sent' : 'delivered',
              timestamp: dateObj.getTime()
            };
          });

          this.mergeHistoryMessages(messages);

          const lastMessage = history[history.length - 1];
          if (lastMessage?.message_date) {
            const lastDate = this.parseApiDate(lastMessage.message_date);
            this.firebaseListenFrom = lastDate.getTime();
          } else {
            this.firebaseListenFrom = null;
          }

          this.startRealtimeListeners();
          // Mark all received messages as seen
          setTimeout(() => this.markAllMessagesAsSeen(), 500);
        } else {
          this.firebaseListenFrom = null;
          this.startRealtimeListeners();
        }
      },
      error: (error) => {
        console.error('Failed to load messages:', error);
        this.firebaseListenFrom = null;
        this.startRealtimeListeners();
      }
    });
  }

  // Start real-time listeners for Firebase events
  private startRealtimeListeners() {
    if (!this.currentUserId || !this.currentStudentId) return;

    const listenerKey = `${this.currentUserId}_${this.currentStudentId}`;
    if (this.isRealtimeListening && this.realtimeListenerKey === listenerKey) {
      return;
    }

    this.firebaseSubscription.unsubscribe();
    this.seenFirebaseMessageIds.clear();
    console.info('[Firebase Direct Chat] Attaching listeners', {
      outgoingPath: `chat_sessions/${this.currentUserId}/${this.currentStudentId}/messages`,
      incomingPath: `chat_sessions/${this.currentStudentId}/${this.currentUserId}/messages`,
      listenFrom: this.firebaseListenFrom
    });

    this.firebaseSubscription = this.firebaseService.listenToDirectMessages(
      this.currentUserId,
      this.currentStudentId,
      this.firebaseListenFrom ?? undefined
    ).subscribe({
      next: (event: FirebaseRealtimeEvent) => {
        this.handleRealtimeEvent(event);
      },
      error: (error) => {
        console.error('Firebase listener error:', error);
      }
    });
    this.isRealtimeListening = true;
    this.realtimeListenerKey = listenerKey;
  }

  // Handle incoming real-time events
  private handleRealtimeEvent(event: FirebaseRealtimeEvent) {
    // Helpful debug log to confirm realtime events are flowing
    console.info('[Firebase Realtime] Event received:', event.type, {
      roomId: event.roomId,
      messageId: event.messageId,
      userId: event.userId,
      data: event.data
    });

    switch (event.type) {
      case 'message':
        this.handleNewMessage(event);
        break;
      case 'message_removed':
        this.handleMessageRemoved(event);
        break;
      case 'typing':
        this.handleTypingEvent(event);
        break;
      case 'read':
        this.handleReadReceipt(event);
        break;
    }
  }

  // Handle new message from Firebase
  private handleNewMessage(event: FirebaseRealtimeEvent) {
    if (!event.data) return;

    if (event.messageId && this.seenFirebaseMessageIds.has(event.messageId)) {
      return;
    }
    if (event.messageId) {
      this.seenFirebaseMessageIds.add(event.messageId);
    }

    const messageData = event.data;
    const existingMessage = this.messagesSubject.value.find(msg => msg.firebaseKey === event.messageId);

    if (!existingMessage) {
      const messageTimestamp = this.resolveMessageTimestamp(messageData);
      const timestamp = new Date(messageTimestamp);
      const isSent = messageData.sender_id?.toString() === this.currentUserId;
      const rawMessage = this.resolveMessageText(messageData);
      const parsed = this.parseMessagePayload(rawMessage);

      const currentMessages = this.messagesSubject.value;
      const optimisticIndex = currentMessages.findIndex(msg =>
        msg.isSent &&
        msg.message === parsed.text &&
        !msg.firebaseKey &&
        Math.abs(this.toMillis(msg.timestamp) - messageTimestamp) < 30000
      );
      if (optimisticIndex >= 0) {
        const updated = [...currentMessages];
        updated[optimisticIndex] = {
          ...updated[optimisticIndex],
          id: event.messageId || updated[optimisticIndex].id,
          status: 'sent',
          firebaseKey: event.messageId,
          timestamp: messageData.timestamp
        };
        this.messagesSubject.next(this.sortMessages(updated));
        return;
      }

      const newMessage: ChatMessage = {
        id: event.messageId || Date.now(),
        name: isSent ? 'You' : (messageData.sender_type === 'student' ? 'Student' : 'User'),
        img: '',
        date: timestamp.toISOString().split('T')[0],
        time: timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        message: parsed.text,
        recipient: 'Chat',
        isSent: isSent,
        type: parsed.type,
        fileUrl: parsed.fileUrl,
        fileName: parsed.fileName,
        status: isSent ? 'sent' : 'delivered',
        isSeen: isSent, // sent messages are considered "seen", received are not until marked
        firebaseKey: event.messageId,
        timestamp: messageTimestamp
      };

      this.upsertSortedMessage(newMessage);
    }
  }

  // Handle message deletion from Firebase
  private handleMessageRemoved(event: FirebaseRealtimeEvent) {
    if (!event.messageId) return;

    const currentMessages = this.messagesSubject.value;
    const filtered = currentMessages.filter(msg => msg.firebaseKey !== event.messageId && msg.id !== event.messageId);

    if (filtered.length !== currentMessages.length) {
      this.messagesSubject.next(filtered);
    }
  }

  // Handle typing indicators
  private handleTypingEvent(event: FirebaseRealtimeEvent) {
    if (!event.data) return;

    const currentTyping = this.typingUsersSubject.value;
    const updatedTyping = { ...currentTyping };

    if (event.data.isTyping) {
      updatedTyping[event.userId || ''] = true;
    } else {
      delete updatedTyping[event.userId || ''];
    }

    this.typingUsersSubject.next(updatedTyping);
  }

  // Handle read receipts
  private handleReadReceipt(event: FirebaseRealtimeEvent) {
    if (!event.messageId) return;

    this.updateMessageStatus(event.messageId!, 'read');
  }

  // Set typing status
  setTyping(isTyping: boolean) {
    if (!this.currentRoomId) return;

    this.firebaseService.setTypingStatus(this.currentRoomId, this.currentUserId, isTyping);
  }

  // Mark message as read
  markMessageAsRead(messageId: number) {
    if (!this.currentRoomId) return;

    this.firebaseService.emitReadReceipt(this.currentRoomId, messageId.toString(), this.currentUserId);
    this.updateMessageStatus(messageId, 'read');
  }

  // Mark all received messages as seen
  markAllMessagesAsSeen() {
    if (!this.currentUserId || !this.currentStudentId) return;

    const messages = this.messagesSubject.value;
    const unreadMessages = messages.filter((msg) => !msg.isSent && !msg.isSeen);

    unreadMessages.forEach((msg) => {
      this.markMessageAsSeen(msg.id);
    });

    this.firebaseService.markConversationMessagesAsSeen(
      this.currentUserId,
      this.currentStudentId,
      this.currentRoomId
    ).catch((error) => {
      console.error('Failed to mark conversation messages as seen:', error);
    });
  }

  // Mark individual message as seen
  markMessageAsSeen(messageId: number | string) {
    if (!this.currentRoomId || !this.currentUserId || !this.currentStudentId) return;

    this.firebaseService.emitSeenReceipt(this.currentRoomId, messageId.toString(), this.currentUserId);
    
    // Update local state
    const messages = this.messagesSubject.value;
    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, isSeen: true } : msg
    );
    this.messagesSubject.next(updatedMessages);
  }

  // Upload file to Firebase Storage
  async uploadFile(file: File): Promise<string> {
    return this.firebaseService.uploadFileToStorage(file, `chat_sessions/${this.currentRoomId}`);
  }

  private buildMessagePayload(message: string, type: 'text' | 'image' | 'file' | 'audio', fileData?: { url?: string; name?: string }) {
    if (type !== 'text' && fileData?.url) {
      return JSON.stringify({
        type,
        text: message,
        fileUrl: fileData.url,
        fileName: fileData.name ?? ''
      });
    }
    return message;
  }

  private buildSendKey(payloadMessage: string, type: 'text' | 'image' | 'file' | 'audio', fileData?: { url?: string; name?: string }) {
    return [
      this.currentUserId,
      this.currentStudentId,
      this.currentUserType,
      type,
      payloadMessage,
      fileData?.url ?? '',
      fileData?.name ?? ''
    ].join('|');
  }

  private parseMessagePayload(raw: string): { text: string; type: 'text' | 'image' | 'file' | 'audio'; fileUrl?: string; fileName?: string } {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.type) {
        return {
          text: parsed.text ?? '',
          type: parsed.type,
          fileUrl: parsed.fileUrl,
          fileName: parsed.fileName
        };
      }
    } catch (e) {
      // ignore parse errors
    }
    return { text: raw, type: 'text' };
  }

  private resolveMessageText(messageData: any): string {
    const rawMessage = messageData?.message
      ?? messageData?.text
      ?? messageData?.body
      ?? messageData?.content
      ?? messageData?.test
      ?? '';

    if (typeof rawMessage === 'string') {
      return rawMessage;
    }

    return JSON.stringify(rawMessage);
  }

  private resolveMessageTimestamp(messageData: any): number {
    return this.toMillis(
      messageData?.timestamp
      ?? messageData?.message_date
      ?? messageData?.created_at
      ?? messageData?.updated_at
      ?? Date.now()
    ) || Date.now();
  }

  private parseApiDate(value?: string | null): Date {
    if (!value) {
      return new Date();
    }

    const normalized = value.replace(' ', 'T');
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized);
    return new Date(hasTimezone ? normalized : `${normalized}+05:30`);
  }

  private toMillis(value?: number | string | null): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    if (typeof value === 'string') {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return numericValue;
      }

      const parsedDate = Date.parse(value);
      return Number.isNaN(parsedDate) ? 0 : parsedDate;
    }

    return 0;
  }

  private sortMessages(messages: ChatMessage[]): ChatMessage[] {
    return [...messages].sort((a, b) => {
      const firstTimestamp = this.toMillis(a.timestamp);
      const secondTimestamp = this.toMillis(b.timestamp);

      if (firstTimestamp !== secondTimestamp) {
        return firstTimestamp - secondTimestamp;
      }

      return a.id.toString().localeCompare(b.id.toString());
    });
  }

  private upsertSortedMessage(message: ChatMessage) {
    const messageKey = message.firebaseKey ?? message.id.toString();
    const nextMessages = [...this.messagesSubject.value];
    const existingIndex = nextMessages.findIndex(existing =>
      (existing.firebaseKey ?? existing.id.toString()) === messageKey
    );

    if (existingIndex >= 0) {
      nextMessages[existingIndex] = {
        ...nextMessages[existingIndex],
        ...message
      };
    } else {
      nextMessages.push(message);
    }

    this.messagesSubject.next(this.sortMessages(nextMessages));
  }

  private removeMessage(messageId: number | string) {
    const filteredMessages = this.messagesSubject.value.filter(message => message.id !== messageId);
    if (filteredMessages.length !== this.messagesSubject.value.length) {
      this.messagesSubject.next(filteredMessages);
    }
  }

  private mergeHistoryMessages(historyMessages: ChatMessage[]) {
    const mergedMessages = [...historyMessages];
    const realtimeMessages = this.messagesSubject.value.filter(message =>
      message.firebaseKey || message.id.toString().startsWith('local_')
    );

    realtimeMessages.forEach(realtimeMessage => {
      const alreadyExists = mergedMessages.some(historyMessage =>
        historyMessage.firebaseKey === realtimeMessage.firebaseKey ||
        (
          historyMessage.message === realtimeMessage.message &&
          historyMessage.isSent === realtimeMessage.isSent &&
          Math.abs(this.toMillis(historyMessage.timestamp) - this.toMillis(realtimeMessage.timestamp)) < 30000
        )
      );

      if (!alreadyExists) {
        mergedMessages.push(realtimeMessage);
      }
    });

    this.messagesSubject.next(this.sortMessages(mergedMessages));
  }

  resetRoom() {
    this.firebaseSubscription.unsubscribe();
    this.firebaseService.disconnect();
    this.currentRoomId = null;
    this.currentStudentId = null;
    this.firebaseListenFrom = null;
    this.messagesSubject.next([]);
    this.seenFirebaseMessageIds.clear();
    this.isRealtimeListening = false;
    this.realtimeListenerKey = null;
  }

  // Cleanup on destroy
  ngOnDestroy() {
    this.firebaseSubscription.unsubscribe();
    this.firebaseService.disconnect();
  }
}
